import React, { useEffect, useState } from "react";
import { wsRequest } from "./wsClient.js";
import { showToast } from "./toast.js";
import OtpCodeInput from "./OtpCodeInput.jsx";

// Three-step TOTP setup flow, shared by the admin panel's mandatory-setup
// interstitial and account settings, and the main site's own account
// settings — the mechanics only live in one place. onComplete fires once
// setup is fully done (after the backup codes have been shown and
// acknowledged), so a caller can dismiss whatever UI wrapped this (an
// interstitial, a modal, an inline section) without needing to know the
// step-by-step details itself.
export default function TotpSetup({ token, onComplete, onCancel }) {
  const [step, setStep] = useState("loading"); // "loading" | "scan" | "backupCodes"
  const [setupData, setSetupData] = useState(null); // { secret, otpauthUrl, qrCodeDataUrl }
  const [code, setCode] = useState("");
  const [attempt, setAttempt] = useState(0); // bumped on each failed confirm, to remount OtpCodeInput fresh (re-focuses box 1)
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    wsRequest("auth.totp.setup", {}, token)
      .then((data) => {
        setSetupData(data);
        setStep("scan");
      })
      .catch((err) => setError(err.message || "Could not reach backend."));
  }, [token]);

  const confirm = async (e, explicitCode) => {
    e?.preventDefault?.();
    const codeToSubmit = explicitCode ?? code;
    setError(null);
    setBusy(true);
    try {
      const data = await wsRequest("auth.totp.confirm", { code: codeToSubmit }, token);
      setBackupCodes(data.backupCodes);
      setStep("backupCodes");
    } catch (err) {
      setError(err.message || "Could not reach backend.");
      setCode("");
      setAttempt((a) => a + 1);
    }
    setBusy(false);
  };

  if (step === "loading") return <p>Setting up…</p>;

  if (step === "scan") {
    return (
      <div style={{ maxWidth: 420 }}>
        <p style={{ fontSize: 13, opacity: 0.8 }}>
          Scan this with your authenticator app (Google Authenticator, Authy, or similar), or enter the code below
          manually if you can't scan.
        </p>
        {setupData?.qrCodeDataUrl && (
          <img
            src={setupData.qrCodeDataUrl}
            alt="Two-factor authentication QR code"
            style={{ display: "block", marginBottom: 12, borderRadius: 8, border: "1px solid var(--border-color)" }}
          />
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
          <code style={{ flex: 1, wordBreak: "break-all", fontSize: 12, opacity: 0.85 }}>{setupData?.secret}</code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(setupData?.secret || "");
              showToast("Copied.");
            }}
          >
            Copy
          </button>
        </div>
        <form onSubmit={confirm}>
          <label style={{ display: "block", fontSize: 13, marginBottom: 8 }}>
            Enter the 6-digit code your app is now showing, to confirm it's set up correctly
          </label>
          <div style={{ marginBottom: 12 }}>
            <OtpCodeInput
              key={attempt}
              length={6}
              value={code}
              onChange={setCode}
              onComplete={(fullCode) => confirm(null, fullCode)}
              autoFocus
              disabled={busy}
            />
          </div>
          {error && <p style={{ color: "var(--accent-red)", fontSize: 13 }}>{error}</p>}
          <button type="submit" disabled={busy || code.length !== 6}>
            {busy ? "…" : "Confirm"}
          </button>{" "}
          {onCancel && (
            <button type="button" onClick={onCancel}>
              Cancel
            </button>
          )}
        </form>
      </div>
    );
  }

  // step === "backupCodes"
  return (
    <div style={{ maxWidth: 420 }}>
      <p style={{ fontSize: 14, fontWeight: 500 }}>Two-factor authentication is on.</p>
      <p style={{ fontSize: 13, opacity: 0.85 }}>
        Save these backup codes somewhere safe — each one can be used once, instead of a code from your app, if you
        ever lose access to it. <strong>They won't be shown again.</strong>
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 6,
          border: "1px solid var(--border-color)",
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
        }}
      >
        {(backupCodes || []).map((c) => (
          <code key={c} style={{ fontSize: 13 }}>
            {c}
          </code>
        ))}
      </div>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText((backupCodes || []).join("\n"));
          showToast("Copied.");
        }}
      >
        Copy all
      </button>{" "}
      <button type="button" onClick={onComplete}>
        Done
      </button>
    </div>
  );
}
